var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/**
 * [CLAIMS_CONTEXT]
 * purpose: Allows the user to select the local origin of a fire damage during the qualification step.
 * touches: declaration, qualification, fireDamage
 * invariants: The selected local fire cause is synchronized with the qualification store ; available choices are provided through configured image choices with optional descriptions
 * inputs: qualification state, fireCauseLocalOrigin, FIRE_CAUSE_LOCAL_ORIGIN_IMAGE_CHOICES, change step
 * outputs: updates the fire damage qualification through updateQualification ; saves the qualification and moves to the next step through updateAndSaveQualification
 * see: @declaration/store/qualification, @declaration/constants/fireDamageConstants, @shared/types/api/claim
 */
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import { UiButton, UiRadio } from '@claims-ia/design-system';
import { QuestionComponent, QuestionProps } from '@declaration/question';
import QuestionPanel from '@declaration/components/UI/QuestionPanel.vue';
import { FireCauseLocalOrigin } from '@shared/types/api/claim';
import { FIRE_CAUSE_LOCAL_ORIGIN_ANSWERS, FIRE_CAUSE_LOCAL_ORIGIN_IMAGE_CHOICES, } from '@declaration/constants/fireDamageConstants';
const qualification = namespace('qualification');
const claim = namespace('claim');
let FireCauseLocalOriginQuestion = class FireCauseLocalOriginQuestion extends QuestionComponent {
    constructor() {
        super(...arguments);
        this.isSaving = false;
        this.typeOfDamages = '';
    }
    get fireCauseLocalOriginChoices() {
        return FIRE_CAUSE_LOCAL_ORIGIN_IMAGE_CHOICES.map((choice) => ({
            value: choice.value,
            label: this.$tc(choice.tradKey),
            description: choice.descriptionTradKey ? this.$tc(choice.descriptionTradKey) : undefined,
            imageSrc: choice.image,
        }));
    }
    get selectedValue() {
        if (this.qualification.fireCauseLocalOrigin === FireCauseLocalOrigin.Inattention) {
            return FireCauseLocalOrigin.Inattention;
        }
        if (this.qualification.fireCauseLocalOrigin === FireCauseLocalOrigin.ElectricalProblem) {
            return FireCauseLocalOrigin.ElectricalProblem;
        }
        if (this.qualification.fireCauseLocalOrigin === FireCauseLocalOrigin.CriminalAct) {
            return FireCauseLocalOrigin.CriminalAct;
        }
        if (this.qualification.fireCauseLocalOrigin === FireCauseLocalOrigin.HotWorks) {
            return FireCauseLocalOrigin.HotWorks;
        }
        if (this.qualification.fireCauseLocalOrigin === FireCauseLocalOrigin.Lightning) {
            return FireCauseLocalOrigin.Lightning;
        }
        if (this.qualification.fireCauseLocalOrigin === FireCauseLocalOrigin.FirePlace) {
            return FireCauseLocalOrigin.FirePlace;
        }
        if (this.qualification.fireCauseLocalOrigin === FireCauseLocalOrigin.dontKnow) {
            return FireCauseLocalOrigin.dontKnow;
        }
        return null;
    }
    get validateText() {
        return this.$tc('claim.damage.fire.button.next_step');
    }
    get panelTitle() {
        return this.$tc('claim.qualification.fire_cause_local_origin.title');
    }
    get sidebarTitle() {
        const key = 'claim.qualification.fire_cause_local_origin.sidebar_title';
        return this.$te(key) ? this.$tc(key) : this.panelTitle;
    }
    get answer() {
        if (this.qualification.fireCauseLocalOrigin === FireCauseLocalOrigin.Inattention) {
            return this.$tc(FIRE_CAUSE_LOCAL_ORIGIN_ANSWERS[FireCauseLocalOrigin.Inattention]);
        }
        if (this.qualification.fireCauseLocalOrigin === FireCauseLocalOrigin.ElectricalProblem) {
            return this.$tc(FIRE_CAUSE_LOCAL_ORIGIN_ANSWERS[FireCauseLocalOrigin.ElectricalProblem]);
        }
        if (this.qualification.fireCauseLocalOrigin === FireCauseLocalOrigin.CriminalAct) {
            return this.$tc(FIRE_CAUSE_LOCAL_ORIGIN_ANSWERS[FireCauseLocalOrigin.CriminalAct]);
        }
        if (this.qualification.fireCauseLocalOrigin === FireCauseLocalOrigin.HotWorks) {
            return this.$tc(FIRE_CAUSE_LOCAL_ORIGIN_ANSWERS[FireCauseLocalOrigin.HotWorks]);
        }
        if (this.qualification.fireCauseLocalOrigin === FireCauseLocalOrigin.Lightning) {
            return this.$tc(FIRE_CAUSE_LOCAL_ORIGIN_ANSWERS[FireCauseLocalOrigin.Lightning]);
        }
        if (this.qualification.fireCauseLocalOrigin === FireCauseLocalOrigin.FirePlace) {
            return this.$tc(FIRE_CAUSE_LOCAL_ORIGIN_ANSWERS[FireCauseLocalOrigin.FirePlace]);
        }
        if (this.qualification.fireCauseLocalOrigin === FireCauseLocalOrigin.dontKnow) {
            return this.$tc(FIRE_CAUSE_LOCAL_ORIGIN_ANSWERS[FireCauseLocalOrigin.dontKnow]);
        }
        return null;
    }
    onTypeOfDamagesClick(fireCauseLocalOrigin) {
        this.updateFireDamageQualification({
            qualificationPayload: {
                fireCauseLocalOrigin: fireCauseLocalOrigin !== this.qualification.fireCauseLocalOrigin
                    ? fireCauseLocalOrigin
                    : null,
            },
        });
    }
    async validate() {
        await this.updateAndSaveFireDamageQualification({
            qualificationPayload: {
                fireCauseLocalOrigin: this.qualification.fireCauseLocalOrigin,
            },
            step: this.step,
        });
    }
};
__decorate([
    claim.State('isUpdatingClaim')
], FireCauseLocalOriginQuestion.prototype, "isUpdatingClaim", void 0);
__decorate([
    qualification.State('qualification')
], FireCauseLocalOriginQuestion.prototype, "qualification", void 0);
__decorate([
    qualification.Action('updateQualification')
], FireCauseLocalOriginQuestion.prototype, "updateFireDamageQualification", void 0);
__decorate([
    qualification.Action('updateAndSaveQualification')
], FireCauseLocalOriginQuestion.prototype, "updateAndSaveFireDamageQualification", void 0);
FireCauseLocalOriginQuestion = __decorate([
    Component({
        components: { QuestionPanel, UiButton, UiRadio },
        props: QuestionProps,
    })
], FireCauseLocalOriginQuestion);
export default FireCauseLocalOriginQuestion;
